// IndexedDB ラッパー
// すべてのストア定義と CRUD ヘルパーをここに集約する
//
// ストア:
//  - tiles  : オフライン地図タイル(key: "layer/z/x/y", value: Blob)
//  - tracks : 軌跡(id, name, points[], stats, createdAt, updatedAt)
//  - plans  : 計画ルート(id, name, waypoints[], stats, createdAt, updatedAt)
//  - poi    : 地点メモ(id, lat, lng, title, body, kind, createdAt)
//  - photos : 写真(id, blob, lat, lng, trackId, takenAt)

const DB_NAME = 'sentier-db';
const DB_VERSION = 4; // v4: contacts/gear/hyakumeizan/checklists 追加

let dbPromise = null;

function openDb() {
  if (dbPromise) return dbPromise;
  dbPromise = new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = (e) => {
      const db = e.target.result;

      // tiles: out-of-line key
      if (!db.objectStoreNames.contains('tiles')) {
        const store = db.createObjectStore('tiles');
        store.createIndex('byLayer', 'layer');
      }

      // tracks
      if (!db.objectStoreNames.contains('tracks')) {
        const store = db.createObjectStore('tracks', { keyPath: 'id' });
        store.createIndex('byUpdatedAt', 'updatedAt');
        store.createIndex('byCreatedAt', 'createdAt');
      }

      // plans
      if (!db.objectStoreNames.contains('plans')) {
        const store = db.createObjectStore('plans', { keyPath: 'id' });
        store.createIndex('byUpdatedAt', 'updatedAt');
      }

      // poi
      if (!db.objectStoreNames.contains('poi')) {
        const store = db.createObjectStore('poi', { keyPath: 'id' });
        store.createIndex('byTrack', 'trackId');
      }

      // photos
      if (!db.objectStoreNames.contains('photos')) {
        const store = db.createObjectStore('photos', { keyPath: 'id' });
        store.createIndex('byTrack', 'trackId');
        store.createIndex('byTakenAt', 'takenAt');
      }

      // v2: AI 提案の履歴
      if (!db.objectStoreNames.contains('suggestions')) {
        const store = db.createObjectStore('suggestions', { keyPath: 'id' });
        store.createIndex('byCreatedAt', 'createdAt');
      }

      // v2: 行きたい山リスト
      if (!db.objectStoreNames.contains('wishlist')) {
        const store = db.createObjectStore('wishlist', { keyPath: 'id' });
        store.createIndex('byAddedAt', 'addedAt');
      }

      // v3: 山名検索の履歴
      if (!db.objectStoreNames.contains('search_history')) {
        const store = db.createObjectStore('search_history', { keyPath: 'id' });
        store.createIndex('byCreatedAt', 'createdAt');
      }

      // v4: 緊急連絡先・同行者
      if (!db.objectStoreNames.contains('contacts')) {
        const store = db.createObjectStore('contacts', { keyPath: 'id' });
        store.createIndex('byCreatedAt', 'createdAt');
      }

      // v4: 装備マスタ
      if (!db.objectStoreNames.contains('gear_master')) {
        const store = db.createObjectStore('gear_master', { keyPath: 'id' });
        store.createIndex('byCategory', 'category');
      }

      // v4: 装備チェックリスト
      if (!db.objectStoreNames.contains('gear_checklists')) {
        const store = db.createObjectStore('gear_checklists', { keyPath: 'id' });
        store.createIndex('byCreatedAt', 'createdAt');
      }

      // v4: 百名山登頂進捗
      if (!db.objectStoreNames.contains('hyakumeizan')) {
        // keyPath なし(山名をキーに put)
        db.createObjectStore('hyakumeizan');
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
  return dbPromise;
}

function promisify(req) {
  return new Promise((resolve, reject) => {
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

async function tx(storeName, mode = 'readonly') {
  const db = await openDb();
  return db.transaction(storeName, mode);
}

// ============ Tiles ============

function tileKey(layer, z, x, y) {
  return `${layer}/${z}/${x}/${y}`;
}

export async function getTile(layer, z, x, y) {
  const t = await tx('tiles');
  const result = await promisify(t.objectStore('tiles').get(tileKey(layer, z, x, y)));
  return result ? result.blob : null;
}

export async function putTile(layer, z, x, y, blob) {
  const t = await tx('tiles', 'readwrite');
  await promisify(t.objectStore('tiles').put({ layer, z, x, y, blob }, tileKey(layer, z, x, y)));
}

export async function hasTile(layer, z, x, y) {
  const t = await tx('tiles');
  const result = await promisify(
    t.objectStore('tiles').getKey(tileKey(layer, z, x, y))
  );
  return result !== undefined;
}

export async function countTiles() {
  const t = await tx('tiles');
  return promisify(t.objectStore('tiles').count());
}

export async function countTilesByLayer(layer) {
  const t = await tx('tiles');
  const index = t.objectStore('tiles').index('byLayer');
  return promisify(index.count(IDBKeyRange.only(layer)));
}

export async function clearTiles() {
  const t = await tx('tiles', 'readwrite');
  await promisify(t.objectStore('tiles').clear());
}

export async function clearTilesByLayer(layer) {
  const t = await tx('tiles', 'readwrite');
  const store = t.objectStore('tiles');
  const index = store.index('byLayer');
  return new Promise((resolve, reject) => {
    const req = index.openKeyCursor(IDBKeyRange.only(layer));
    req.onerror = () => reject(req.error);
    req.onsuccess = (e) => {
      const cursor = e.target.result;
      if (cursor) {
        store.delete(cursor.primaryKey);
        cursor.continue();
      } else {
        resolve();
      }
    };
  });
}

// ============ Tracks ============

export async function getAllTracks() {
  const t = await tx('tracks');
  const all = await promisify(t.objectStore('tracks').getAll());
  return all.sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0));
}

export async function getTrack(id) {
  const t = await tx('tracks');
  return promisify(t.objectStore('tracks').get(id));
}

export async function saveTrack(track) {
  const now = Date.now();
  const data = {
    ...track,
    id: track.id || crypto.randomUUID(),
    createdAt: track.createdAt || now,
    updatedAt: now,
  };
  const t = await tx('tracks', 'readwrite');
  await promisify(t.objectStore('tracks').put(data));
  return data;
}

export async function deleteTrack(id) {
  const t = await tx('tracks', 'readwrite');
  await promisify(t.objectStore('tracks').delete(id));
}

// ============ Plans ============

export async function getAllPlans() {
  const t = await tx('plans');
  const all = await promisify(t.objectStore('plans').getAll());
  return all.sort((a, b) => (b.updatedAt || 0) - (a.updatedAt || 0));
}

export async function getPlan(id) {
  const t = await tx('plans');
  return promisify(t.objectStore('plans').get(id));
}

export async function savePlan(plan) {
  const now = Date.now();
  const data = {
    ...plan,
    id: plan.id || crypto.randomUUID(),
    createdAt: plan.createdAt || now,
    updatedAt: now,
  };
  const t = await tx('plans', 'readwrite');
  await promisify(t.objectStore('plans').put(data));
  return data;
}

export async function deletePlan(id) {
  const t = await tx('plans', 'readwrite');
  await promisify(t.objectStore('plans').delete(id));
}

// ============ POI ============

export async function getAllPoi(trackId) {
  const t = await tx('poi');
  if (trackId) {
    const index = t.objectStore('poi').index('byTrack');
    return promisify(index.getAll(IDBKeyRange.only(trackId)));
  }
  return promisify(t.objectStore('poi').getAll());
}

export async function savePoi(poi) {
  const data = {
    ...poi,
    id: poi.id || crypto.randomUUID(),
    createdAt: poi.createdAt || Date.now(),
  };
  const t = await tx('poi', 'readwrite');
  await promisify(t.objectStore('poi').put(data));
  return data;
}

export async function deletePoi(id) {
  const t = await tx('poi', 'readwrite');
  await promisify(t.objectStore('poi').delete(id));
}

// ============ Photos ============

export async function getPhotosByTrack(trackId) {
  const t = await tx('photos');
  const index = t.objectStore('photos').index('byTrack');
  return promisify(index.getAll(IDBKeyRange.only(trackId)));
}

export async function savePhoto(photo) {
  const data = {
    ...photo,
    id: photo.id || crypto.randomUUID(),
    takenAt: photo.takenAt || Date.now(),
  };
  const t = await tx('photos', 'readwrite');
  await promisify(t.objectStore('photos').put(data));
  return data;
}

export async function deletePhoto(id) {
  const t = await tx('photos', 'readwrite');
  await promisify(t.objectStore('photos').delete(id));
}

// ============ AI 提案履歴 ============

export async function getAllSuggestions() {
  const t = await tx('suggestions');
  const all = await promisify(t.objectStore('suggestions').getAll());
  return all.sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0));
}

export async function getSuggestion(id) {
  const t = await tx('suggestions');
  return promisify(t.objectStore('suggestions').get(id));
}

export async function saveSuggestion(suggestion) {
  const data = {
    ...suggestion,
    id: suggestion.id || crypto.randomUUID(),
    createdAt: suggestion.createdAt || Date.now(),
  };
  const t = await tx('suggestions', 'readwrite');
  await promisify(t.objectStore('suggestions').put(data));
  return data;
}

export async function deleteSuggestion(id) {
  const t = await tx('suggestions', 'readwrite');
  await promisify(t.objectStore('suggestions').delete(id));
}

// ============ 行きたい山リスト(Wishlist) ============

export async function getAllWishlist() {
  const t = await tx('wishlist');
  const all = await promisify(t.objectStore('wishlist').getAll());
  return all.sort((a, b) => (b.addedAt || 0) - (a.addedAt || 0));
}

export async function getWishlistByName(name) {
  // 名前で重複チェックする用
  const all = await getAllWishlist();
  return all.find((w) => w.name === name);
}

export async function addToWishlist(mountain) {
  const data = {
    ...mountain,
    id: mountain.id || crypto.randomUUID(),
    addedAt: mountain.addedAt || Date.now(),
  };
  const t = await tx('wishlist', 'readwrite');
  await promisify(t.objectStore('wishlist').put(data));
  return data;
}

export async function deleteWishlist(id) {
  const t = await tx('wishlist', 'readwrite');
  await promisify(t.objectStore('wishlist').delete(id));
}

// ============ 山名検索の履歴 ============

export async function getAllSearchHistory() {
  const t = await tx('search_history');
  const all = await promisify(t.objectStore('search_history').getAll());
  return all.sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0));
}

export async function saveSearchHistory(entry) {
  const data = {
    ...entry,
    id: entry.id || crypto.randomUUID(),
    createdAt: entry.createdAt || Date.now(),
  };
  const t = await tx('search_history', 'readwrite');
  await promisify(t.objectStore('search_history').put(data));
  return data;
}

export async function deleteSearchHistory(id) {
  const t = await tx('search_history', 'readwrite');
  await promisify(t.objectStore('search_history').delete(id));
}

// ============ 緊急連絡先・同行者 ============

export async function getAllContacts() {
  const t = await tx('contacts');
  const all = await promisify(t.objectStore('contacts').getAll());
  return all.sort((a, b) => (a.createdAt || 0) - (b.createdAt || 0));
}

export async function saveContact(c) {
  const data = {
    ...c,
    id: c.id || crypto.randomUUID(),
    createdAt: c.createdAt || Date.now(),
  };
  const t = await tx('contacts', 'readwrite');
  await promisify(t.objectStore('contacts').put(data));
  return data;
}

export async function deleteContact(id) {
  const t = await tx('contacts', 'readwrite');
  await promisify(t.objectStore('contacts').delete(id));
}

// ============ 装備マスタ ============

export async function getAllGear() {
  const t = await tx('gear_master');
  const all = await promisify(t.objectStore('gear_master').getAll());
  return all.sort((a, b) => (a.category || '').localeCompare(b.category || '') || (a.name || '').localeCompare(b.name || ''));
}

export async function saveGear(g) {
  const data = {
    ...g,
    id: g.id || crypto.randomUUID(),
    createdAt: g.createdAt || Date.now(),
  };
  const t = await tx('gear_master', 'readwrite');
  await promisify(t.objectStore('gear_master').put(data));
  return data;
}

export async function deleteGear(id) {
  const t = await tx('gear_master', 'readwrite');
  await promisify(t.objectStore('gear_master').delete(id));
}

// ============ 装備チェックリスト ============

export async function getAllChecklists() {
  const t = await tx('gear_checklists');
  const all = await promisify(t.objectStore('gear_checklists').getAll());
  return all.sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0));
}

export async function getChecklist(id) {
  const t = await tx('gear_checklists');
  return promisify(t.objectStore('gear_checklists').get(id));
}

export async function saveChecklist(c) {
  const data = {
    ...c,
    id: c.id || crypto.randomUUID(),
    createdAt: c.createdAt || Date.now(),
    updatedAt: Date.now(),
  };
  const t = await tx('gear_checklists', 'readwrite');
  await promisify(t.objectStore('gear_checklists').put(data));
  return data;
}

export async function deleteChecklist(id) {
  const t = await tx('gear_checklists', 'readwrite');
  await promisify(t.objectStore('gear_checklists').delete(id));
}

// ============ 百名山登頂進捗 ============

export async function getHyakumeizanProgress() {
  const t = await tx('hyakumeizan');
  const store = t.objectStore('hyakumeizan');
  // 全エントリ取得
  return new Promise((resolve, reject) => {
    const req = store.openCursor();
    const result = {};
    req.onerror = () => reject(req.error);
    req.onsuccess = (e) => {
      const cur = e.target.result;
      if (cur) {
        result[cur.key] = cur.value;
        cur.continue();
      } else {
        resolve(result);
      }
    };
  });
}

export async function markHyakumeizan(mountainName, trackId, summitTs) {
  const t = await tx('hyakumeizan', 'readwrite');
  await promisify(t.objectStore('hyakumeizan').put({ trackId, summitTs }, mountainName));
}

export async function clearHyakumeizan(mountainName) {
  const t = await tx('hyakumeizan', 'readwrite');
  await promisify(t.objectStore('hyakumeizan').delete(mountainName));
}

// ============ フルバックアップ/復元 ============

const BACKUP_STORES = ['tracks', 'plans', 'poi', 'photos', 'suggestions', 'wishlist', 'search_history', 'contacts', 'gear_master', 'gear_checklists', 'hyakumeizan'];

// 写真の Blob は別途 base64 化が必要
export async function exportFullBackup() {
  const db = await openDb();
  const out = { version: DB_VERSION, generatedAt: Date.now(), stores: {} };

  for (const name of BACKUP_STORES) {
    if (!db.objectStoreNames.contains(name)) continue;
    const transaction = db.transaction(name);
    const store = transaction.objectStore(name);
    if (name === 'hyakumeizan') {
      // keyPath なしの out-of-line key
      const entries = await new Promise((resolve, reject) => {
        const records = [];
        const req = store.openCursor();
        req.onsuccess = (e) => {
          const cur = e.target.result;
          if (cur) {
            records.push({ key: cur.key, value: cur.value });
            cur.continue();
          } else resolve(records);
        };
        req.onerror = () => reject(req.error);
      });
      out.stores[name] = entries;
    } else if (name === 'photos') {
      // Blob を base64 化
      const raw = await promisify(store.getAll());
      out.stores[name] = await Promise.all(raw.map(async (p) => ({
        ...p,
        blob: p.blob ? await blobToBase64(p.blob) : null,
        _blobIsBase64: true,
      })));
    } else {
      out.stores[name] = await promisify(store.getAll());
    }
  }

  return out;
}

export async function importFullBackup(data, options = { mergeMode: 'replace' }) {
  if (!data || !data.stores) throw new Error('バックアップデータが不正です');
  const db = await openDb();
  for (const name of BACKUP_STORES) {
    if (!data.stores[name] || !db.objectStoreNames.contains(name)) continue;
    const transaction = db.transaction(name, 'readwrite');
    const store = transaction.objectStore(name);

    if (options.mergeMode === 'replace') {
      await promisify(store.clear());
    }

    const items = data.stores[name];
    if (name === 'hyakumeizan') {
      for (const it of items) {
        await promisify(store.put(it.value, it.key));
      }
    } else if (name === 'photos') {
      for (const p of items) {
        const fixed = { ...p };
        if (p._blobIsBase64 && p.blob) {
          fixed.blob = await base64ToBlob(p.blob, p.mimeType || 'image/jpeg');
          delete fixed._blobIsBase64;
        }
        await promisify(store.put(fixed));
      }
    } else {
      for (const it of items) {
        await promisify(store.put(it));
      }
    }
  }
}

function blobToBase64(blob) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(blob);
  });
}

async function base64ToBlob(dataUrl, fallbackType = 'image/jpeg') {
  const res = await fetch(dataUrl);
  return res.blob();
}

// ============ ストレージ使用量 ============

export async function getStorageEstimate() {
  if (navigator.storage && navigator.storage.estimate) {
    return navigator.storage.estimate();
  }
  return { usage: 0, quota: 0 };
}
